import { useCallback, useMemo, useState } from 'react';
import { SearchInput } from '../SearchInput';
import SweepSelect from '../SweepSelect';
import StyledTooltip from '../StyledTooltip';
import keyBy from 'lodash/keyBy';
import { Box, MenuItem, SelectProps } from '@mui/material';
import { SearchSelectListSubheader } from './SearchSelectListSubheader';
import { SearchSelectProps, SearchSelectItem } from './selectTypes';
import { defaultItemRenderer, DEFAULT_EMPTY_RESULTS_MESSAGE, maybeRenderEmptyResults } from './selectUtils';

// Re-export types for convenience
export type { SearchSelectItem, SearchSelectProps } from './selectTypes';

export function SearchSelect<T = any>({
  items,
  valueRenderer,
  onChange,
  searchPlaceholder,
  children,
  itemRenderer = defaultItemRenderer,
  emptyResultsMessage = DEFAULT_EMPTY_RESULTS_MESSAGE,
  ...sweepSelectProps
}: SearchSelectProps<T>) {
  const [search, setSearch] = useState('');
  const itemsByKey = useMemo(() => keyBy(items, 'value'), [items]);

  const localItems = useMemo(() => {
    return items.filter((item) => {
      return (
        sweepSelectProps.SelectProps?.value === item.value ||
        item.label.toUpperCase().includes(search.toUpperCase())
      );
    });
  }, [items, sweepSelectProps.SelectProps?.value, search]);

  const selectProps: SelectProps = useMemo(() => {
    return {
      ...sweepSelectProps.SelectProps,
      onChange: (event) => {
        const item = itemsByKey[event.target.value as string];
        item && onChange && onChange(item);
      },
      inputProps: {
        ...sweepSelectProps.SelectProps?.inputProps,
        onChange: (event) => {
          sweepSelectProps.SelectProps?.inputProps?.onChange &&
            sweepSelectProps.SelectProps?.inputProps?.onChange(event);
        },
      },
    };
  }, [itemsByKey, onChange, sweepSelectProps.SelectProps]);

  const renderValue = useCallback(
    (value: unknown) => {
      const item = items.find((item) => item.value === value);
      if (valueRenderer) {
        return valueRenderer(item as SearchSelectItem<T>);
      }
      return <>{item?.label || (value as any)}</>;
    },
    [items, valueRenderer],
  );



  return (
    <SweepSelect
      {...sweepSelectProps}
      MenuProps={{
        ...sweepSelectProps.MenuProps,
        autoFocus: false,
        sx: {
          '.MuiList-root': {
            paddingTop: 0,
          },
        },
      }}
      SelectProps={selectProps}
      renderValue={renderValue}
      onOpenCb={(event) => {
        setSearch('');
        sweepSelectProps.onOpenCb && sweepSelectProps.onOpenCb(event);
      }}
    >
      {!Boolean(sweepSelectProps.isLoading) && !children && (
        <SearchSelectListSubheader>
          <SearchInput
            withFixedMagnifyingGlassIcon
            placeholder={searchPlaceholder}
            TextFieldProps={{
              sx: { minWidth: '270px' },
              value: search,
              autoFocus: true,
              onChange: (event) => {
                const value = event.target.value;
                setSearch(value);
              },
              onKeyDown: (e) => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              },
              onClick: (e) => {
                e.stopPropagation();
              },
            }}
          />
        </SearchSelectListSubheader>
      )}
      {maybeRenderEmptyResults(emptyResultsMessage, localItems.length > 0)}
      {!children && <MenuItem value="" hidden />}
      {!children &&
        localItems.map((item) => (
            <MenuItem
              value={item.value}
              key={item.value}
              autoFocus={item.value === sweepSelectProps.SelectProps?.value}
              disabled={item.disabled}
            >
              <StyledTooltip
                title={item.tooltip}
                PopperProps={{
                  sx: {
                    zIndex: 999999, // Needs to be higher than the dropdown of the select
                  },
                }}
                placement="right"
              >
                <Box>{itemRenderer ? itemRenderer(item, search) : item.label}</Box>
              </StyledTooltip>
            </MenuItem>
          ),
        )}
      {children}
    </SweepSelect>
  );
}
